// The AI packaging prompt, exported as a constant so the builder UI can offer
// a "Copy prompt for your AI" button (plan §9). This file is the prompt's
// source of truth.

export const CREATIVE_IMPORT_PACKAGING_PROMPT = `We're done creating. Now package the approved ad creatives from this chat for import into my ad tool. Do not create or modify any creative content in this step.

**What to include:** preserve the assets, formats, groupings, order, and final copy I approved or clearly selected. Leave out rejected versions and drafts unless I say otherwise. Only use carousel or dynamic when I explicitly established that format; otherwise each ad is its own creative. If anything is genuinely unclear, ask me before packaging.

**Ads that come in several sizes:** when one ad exists in several sizes, package it as one \`per_placement\` creative, not as separate creatives. Its 4:5 file is the \`feed\` file and its 9:16 file is the \`story\` file (Stories and Reels); if it has no 4:5, its 1:1 is the \`feed\` file. Leave its other sizes out. An ad with only one size is packaged as \`single_image\` or \`single_video\`, exactly as it would be otherwise. Never crop, resize, or create a missing size, and never fill a placement with a file from a different ad: pair files only when they are clearly the same ad in different sizes, and if you can't tell, ask me. Carousel and dynamic creatives can't carry per-placement sizes: use one size of each image or video (its 4:5, else its 1:1) and leave the rest out.

**Two rules that override everything below:**
1. **Copy and factual fields are transcription-only.** Include only values I approved or explicitly stated in this chat, verbatim. Omit everything else. Most creatives will have only some copy fields — a sparse \`copy\` object is correct output, not an incomplete job.
2. **\`justification\` is the exception — always include it.** Infer the apparent strategic purpose from observable choices in this chat: the angle, the format, what the imagery emphasizes, how this ad differs from its siblings, and the audience *as discussed here*. Never introduce unstated audiences, offers, performance claims, or business facts.

**Build this structure and zip it:**
\`\`\`
<a short folder name of your choosing that describes this batch>/
  creatives.json
  media/
    <the image and video files>
\`\`\`
Name that top folder whatever describes this batch — the importer ignores it, so pick something meaningful rather than reusing an existing folder. \`creatives.json\` and \`media/\` must be named exactly as shown.

**creatives.json format** — UTF-8 JSON using this shape:
\`\`\`json
{
  "manifest_version": 1,
  "creatives": [
    {
      "creative_name": "Fall Roast Launch",
      "format": "single_image",
      "media_path": "media/fall-roast.jpg",
      "justification": "Why we chose this ad, in my own words from this chat",
      "copy": {
        "primary_text": "Final primary text from this chat",
        "headline": "Final headline from this chat",
        "description": "Final description from this chat",
        "call_to_action": "SHOP_NOW",
        "destination_url": "https://example.com/fall-roast",
        "url_parameters": "utm_source=facebook&utm_campaign=fall_roast"
      }
    },
    {
      "creative_name": "Fall Roast Teaser Video",
      "format": "single_video",
      "media_path": "media/fall-roast-teaser.mp4",
      "justification": "Motion-first take on the same launch, leading with the pour to earn attention in feed",
      "copy": {
        "primary_text": "The only copy I approved for this one"
      }
    },
    {
      "creative_name": "Fall Roast Launch — All Placements",
      "format": "per_placement",
      "media": [
        { "media_path": "media/fall-roast-4x5.jpg", "placement": "feed" },
        { "media_path": "media/fall-roast-9x16.jpg", "placement": "story" }
      ],
      "justification": "The launch ad sized for each placement, so Stories and Reels get the full-height cut",
      "copy": {
        "primary_text": "Final primary text from this chat",
        "headline": "Final headline from this chat"
      }
    },
    {
      "creative_name": "Fall Product Carousel",
      "format": "carousel",
      "copy": {
        "primary_text": "Final shared carousel text from this chat",
        "call_to_action": "SHOP_NOW",
        "destination_url": "https://example.com/fall"
      },
      "slides": [
        {
          "media_path": "media/fall-roast.jpg",
          "copy": {
            "headline": "Fall Roast",
            "description": "Small-batch seasonal roast"
          }
        },
        {
          "media_path": "media/fall-mug.jpg",
          "copy": {
            "headline": "Limited Edition Mug",
            "destination_url": "https://example.com/fall-mug"
          }
        }
      ]
    },
    {
      "creative_name": "Fall Collection Dynamic",
      "format": "dynamic",
      "media_paths": [
        "media/fall-roast.jpg",
        "media/fall-mug.jpg",
        "media/fall-collection.gif"
      ],
      "copy": {
        "primary_texts": ["First final primary text", "Second final primary text"],
        "headlines": ["First final headline", "Second final headline"],
        "descriptions": ["First final description", "Second final description"],
        "call_to_actions": ["SHOP_NOW", "LEARN_MORE"],
        "destination_url": "https://example.com/fall"
      }
    }
  ]
}
\`\`\`
Rules:
- \`format\` is required and must be \`single_image\`, \`single_video\`, \`carousel\`, \`dynamic\`, or \`per_placement\`. A single video uses the same shape as a single image with \`format\`: \`single_video\`. Treat .gif as video.
- Use \`media_path\` for a single creative or carousel slide, \`media_paths\` for a dynamic creative, and \`media\` for a per_placement creative: one entry per size used, each with its \`media_path\` and a \`placement\` of \`feed\` or \`story\`. Paths are relative and must exactly match file paths and casing inside the zip. Supported files: .jpg/.jpeg/.png images and .mp4/.mov/.gif videos.
- Carousel \`slides\` are ordered and contain 2–5 items. Dynamic media and copy arrays are independent pools that may be freely recombined.
- Give each creative a short, human-findable \`creative_name\`, preferring an established name from this chat.
- \`justification\` belongs on the creative, not on a slide: one or two sentences on why this ad exists. Where I stated the reasoning, carry it over verbatim or lightly condensed; otherwise infer it per rule 2 above. It asserts intent, never facts.
- All \`copy\` fields are optional. Include final copy already approved or clearly selected in this chat, verbatim and attached at the correct creative or carousel-slide level. Never draft, rewrite, improve, or fill in missing copy during packaging; omit absent fields or the whole \`copy\` object.

Give me the final \`.zip\` to download.`;
